import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Avatar components with exact Hyperkit styling
 *
 * Hyperkit Specs:
 * - Sizes: xs=24px, sm=32px, md=40px, lg=48px, xl=64px
 * - Border Radius: Full (rounded-full)
 * - Fallback: Grey bg with initials
 */

const avatarVariants = cva(
  'relative flex shrink-0 overflow-hidden rounded-full',
  {
    variants: {
      size: {
        xs: 'h-6 w-6',    // 24px
        sm: 'h-8 w-8',    // 32px
        default: 'h-10 w-10', // 40px
        lg: 'h-12 w-12',  // 48px
        xl: 'h-16 w-16',  // 64px
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const avatarFallbackVariants = cva(
  'flex h-full w-full items-center justify-center rounded-full bg-grey-100 text-grey-600 font-medium dark:bg-grey-800 dark:text-grey-300',
  {
    variants: {
      size: {
        xs: 'text-[10px]',
        sm: 'text-xs',
        default: 'text-sm',
        lg: 'text-base',
        xl: 'text-lg',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

const Avatar = React.forwardRef(({ className, size, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(avatarVariants({ size }), className)}
    {...props}
  />
));
Avatar.displayName = 'Avatar';

const AvatarImage = React.forwardRef(({ className, src, alt, ...props }, ref) => (
  <img
    ref={ref}
    src={src}
    alt={alt}
    className={cn('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = 'AvatarImage';

const AvatarFallback = React.forwardRef(({ className, size, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(avatarFallbackVariants({ size }), className)}
    {...props}
  />
));
AvatarFallback.displayName = 'AvatarFallback';

export { Avatar, AvatarImage, AvatarFallback, avatarVariants };
